import {emptyArray} from "../constants";
import ROUTE_BUILDING_STRATEGY from "../constants/routeBuildingStrategy";
import NODE_TYPES from "../constants/nodeTypes";
import RouteRequest from "../models/RouteRequest";

export const isEqual = (value, other) => {
  // Get the value type
  const type = Object.prototype.toString.call(value);

  // If the two objects are not the same type, return false
  if (type !== Object.prototype.toString.call(other)) return false;

  // If items are not an object or array, return false
  if (["[object Array]", "[object Object]"].indexOf(type) < 0) return false;

  // Compare the length of the length of the two items
  const valueLen = type === "[object Array]" ? value.length : Object.keys(value).length;
  const otherLen = type === "[object Array]" ? other.length : Object.keys(other).length;
  if (valueLen !== otherLen) return false;

  // Compare two items
  const compare = function (item1, item2) {
    // Get the object type
    const itemType = Object.prototype.toString.call(item1);

    // If an object or array, compare recursively
    if (["[object Array]", "[object Object]"].indexOf(itemType) >= 0) {
      if (!isEqual(item1, item2)) return false;
    } else {
      // If the two items are not the same type, return false
      if (itemType !== Object.prototype.toString.call(item2)) return false;

      // Else if it's a function, convert to a string and compare
      // Otherwise, just compare
      if (itemType === "[object Function]") {
        if (item1.toString() !== item2.toString()) return false;
      } else if (item1 !== item2) return false;
    }

    return true;
  };
  // Compare properties
  if (type === "[object Array]") {
    for (let i = 0; i < valueLen; i += 1) {
      if (compare(value[i], other[i]) === false) return false;
    }
  } else {
    const keys = Object.keys(value);
    const anyNotEq = keys.some(key => value.hasOwnProperty(key) && compare(value[key], other[key]) === false);

    if (anyNotEq) return false;
  }
  // If nothing failed, return true
  return true;
};

/** Убирает только подряд идущие дубликаты по значению */
export const uniqueByConsecutiveValues = (arr) => {
  if (arr.length === 0) return arr;

  const result = [arr[0]];

  for (let i = 1; i < arr.length; i++) {
    const prev = result[result.length - 1];
    const curr = arr[i];

    if (!isEqual(prev, curr)) {
      result.push(curr);
    }
  }

  return result;
};

/**
 * Делает "виртуальный inner join" по ключам obj и anotherObj.
 * Возвращает объект, содержащий только те свойства, которые есть в обоих.
 */
export const intersectObjects = (obj, anotherObj) =>{
  const result = {};

  for (const key of Object.keys(obj)) {
    if (!(key in anotherObj)) continue;

    const errVal = obj[key];
    const touchedVal = anotherObj[key];

    // если оба значения объекты → рекурсивный спуск
    if (
      errVal &&
      touchedVal &&
      typeof errVal === "object" &&
      typeof touchedVal === "object" &&
      !Array.isArray(errVal) &&
      !Array.isArray(touchedVal)
    ) {
      const nested = intersectObjects(errVal, touchedVal);
      if (Object.keys(nested).length > 0) {
        result[key] = nested;
      }
    } else {
      // если anotherObj не объект → это "флажок", значит берем error
      result[key] = errVal;
    }
  }

  return result;
}

/**
 * @param {SearchRouteParameters} searchParameters Объект с параметрами поиска
 */
export const extractOptionsFromSearchParams = searchParameters => {
  const restrictions = searchParameters.restrictions;
  return {
    withinCountry: restrictions.withinCountry,
    excludeMkad: restrictions.excludeMkad,
    allowFerry: restrictions.allowFerries,
    allowWinterRoad: restrictions.allowWinterRoads,
    allowTollRoad: !restrictions.excludeTollRoads,
    strategy: restrictions.fastWay ? ROUTE_BUILDING_STRATEGY.Fastest : ROUTE_BUILDING_STRATEGY.Shortest,
    weight: restrictions.weight
  };
};

/**
 * @param {SearchRouteParameters} searchParameters Объект с параметрами поиска
 */
export const extractNodesFromSearchParams = searchParameters => {
  const routeRequestAllPoints = RouteRequest.getAllRoutePoints(searchParameters.routeRequest);
  const nodes = new Array(routeRequestAllPoints.length);
  for (let i = 0; i < routeRequestAllPoints.length; i++) {
    const routeRequestPoint = routeRequestAllPoints[i];
    nodes[i] = {
      nodeId: routeRequestPoint.getCityId(),
      type: getNodeType(routeRequestPoint),
      geoPoint: routeRequestPoint.geoPoint
    };
  }
  return nodes;
};

/** @param {RouteRequestPoint} routeRequestPoint
 * @returns {number}
 */
const getNodeType = (routeRequestPoint) => {
  let type = 0;
  if (routeRequestPoint.isStartPoint) {
    type |= NODE_TYPES.start;
  }
  if (routeRequestPoint.isLoadingPoint) {
    type |= NODE_TYPES.load;
  }
  if (routeRequestPoint.isIntermediatePoint) {
    type |= NODE_TYPES.passthrough;
  }
  if (routeRequestPoint.isUnloadingPoint) {
    type |= NODE_TYPES.unload;
  }
  if (routeRequestPoint.isEndPoint) {
    type |= NODE_TYPES.finish;
  }
  return type;
};

/**
 * @param routeRequest {RouteRequest}
 * @param cityId {number}
 * @returns {RouteRequest}
 */
export const excludeIntermediateCityFromRoute = (routeRequest, cityId) => {
  const through = routeRequest.through.filter(routePoint => routePoint.getCityId() !== cityId);
  return {
    ...routeRequest,
    through: through.length === 0 ? emptyArray : through,
  };
};


/**
 * @param routeRequest {RouteRequest}
 * @param index {number} Индекс промежуточной точки
 * @returns {RouteRequest}
 */
export const excludePassthroughPointFromRoute = (routeRequest, index) => {
  const through = routeRequest.through;
  const newThrough = [...through.slice(0, index), ...through.slice(index + 1)];
  return {...routeRequest, through: newThrough};
}